import { useState } from "react";
import { Link } from "react-router-dom";
import {
  HandHeart,
  KeyRound,
  Home as HomeIcon,
  Headphones,
  LineChart,
  Check,
  ArrowRight,
  MapPin,
  Plus,
  Minus,
} from "lucide-react";
import { PageHero, CTASection } from "../components/sections";
import { Reveal, Eyebrow, SectionHeader } from "../components/common";
import { IMAGES, ZONES, CONCIERGERIE_FAQ } from "../data/site";
import Seo from "../components/Seo";
import { SEO } from "../data/seo";

const blocks = [
  {
    icon: <HandHeart size={22} />,
    title: "Accueil des voyageurs",
    text: "Un accueil personnalisé et chaleureux pour démarrer le séjour de vos voyageurs dans les meilleures conditions.",
    items: ["Accueil physique ou autonome", "Présentation du logement", "Remise des clés", "Informations locales", "Disponibilité pour les questions"],
  },
  {
    icon: <KeyRound size={22} />,
    title: "Gestion des clés",
    text: "Gestion sécurisée et flexible des accès à votre bien.",
    items: ["Boîte à clés sécurisée", "Serrure connectée possible", "Double de sécurité", "Intervention en cas de perte", "Check-in flexible"],
  },
  {
    icon: <HomeIcon size={22} />,
    title: "Préparation du logement",
    text: "Votre bien est préparé avec soin pour offrir une expérience parfaite à chaque arrivée.",
    items: ["Vérification générale", "Contrôle de la température", "Éclairage d'accueil", "Produits de bienvenue", "Vérification des équipements"],
  },
  {
    icon: <Headphones size={22} />,
    title: "Assistance pendant le séjour",
    text: "Un support réactif pour répondre à toutes les demandes pendant le séjour.",
    items: ["Hotline 7j/7", "Gestion des urgences", "Coordination des prestataires", "Recommandations locales", "Résolution de problèmes"],
  },
  {
    icon: <LineChart size={22} />,
    title: "Suivi propriétaire",
    text: "Restez informé en temps réel de l'activité sur votre bien.",
    items: ["Comptes-rendus réguliers", "Photos après prestations", "Alertes en cas d'anomalie", "Suggestions d'amélioration", "Rapport mensuel"],
  },
];

export default function Conciergerie() {
  return (
    <div data-testid="conciergerie-page">
      <Seo {...SEO.conciergerie} />
      <PageHero
        eyebrow="Services"
        title="Conciergerie <em style='color:var(--gold-2)'>Haut de Gamme</em>"
        sub="Un accompagnement sur-mesure pour une gestion sereine de vos locations. De l'accueil des voyageurs au suivi personnalisé de votre bien."
        image={IMAGES.luxLiving}
      />

      <section className="section">
        <div className="container-x grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <img src={IMAGES.bedroom} alt="Logement préparé" className="w-full object-cover" style={{ height: 480 }} />
          </Reveal>
          <div>
            <Reveal><Eyebrow>Votre sérénité</Eyebrow></Reveal>
            <Reveal delay={80}>
              <h2 className="display-title mt-4 text-4xl md:text-5xl">
                Votre tranquillité, notre priorité
              </h2>
            </Reveal>
            <Reveal delay={140}>
              <p className="mt-5 leading-relaxed" style={{ color: "var(--muted)" }}>
                Gérer une location saisonnière demande du temps, de la disponibilité
                et une expertise locale. Notre service de conciergerie vous libère de
                ces contraintes.
              </p>
            </Reveal>
            <Reveal delay={200}>
              <p className="mt-4 leading-relaxed" style={{ color: "var(--muted)" }}>
                De l'accueil de vos voyageurs à la gestion des imprévus, nous prenons
                en charge tous les aspects opérationnels de votre location. Vous restez
                propriétaire, nous gérons le quotidien.
              </p>
            </Reveal>
            <div className="flex flex-wrap gap-3 mt-7">
              {["Disponible 7j/7", "Interlocuteur dédié", "Suivi personnalisé"].map((t) => (
                <span key={t} className="flex items-center gap-2 px-4 py-2 text-sm" style={{ background: "var(--cream)" }}>
                  <Check size={15} style={{ color: "var(--gold)" }} /> {t}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="section" style={{ background: "var(--cream)" }}>
        <div className="container-x">
          <SectionHeader eyebrow="Nos prestations" title="Un accompagnement complet" />
          <div className="flex flex-wrap justify-center gap-8 mt-14">
            {blocks.map((b, i) => (
              <Reveal key={b.title} delay={i * 180} className="w-full md:w-[calc(50%-1rem)] lg:w-[calc(33.333%-1.34rem)]">
                <div className="p-8 h-full" style={{ background: "#fff", border: "1px solid var(--line)" }}>
                  <div className="flex items-center justify-center" style={{ width: 50, height: 50, background: "var(--gold)", color: "#fff" }}>
                    {b.icon}
                  </div>
                  <h3 className="font-display text-2xl mt-5">{b.title}</h3>
                  <p className="mt-2 text-sm" style={{ color: "var(--muted)" }}>{b.text}</p>
                  <ul className="mt-5 space-y-2">
                    {b.items.map((it) => (
                      <li key={it} className="flex items-center gap-2 text-sm">
                        <Check size={15} style={{ color: "var(--gold)" }} /> {it}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container-x">
          <Reveal>
            <div className="p-10 md:p-14 text-center" style={{ background: "var(--ink)" }}>
              <Eyebrow light>Tarif simple</Eyebrow>
              <div className="font-display text-white mt-4 text-6xl">20% <span className="text-2xl">TTC</span></div>
              <p className="mt-3" style={{ color: "rgba(255,255,255,0.7)" }}>par nuitée · + ménage à la charge du locataire</p>
              <p className="mt-2 text-sm" style={{ color: "rgba(255,255,255,0.5)" }}>
                Sans frais cachés • Gestion optimisée • Conciergerie professionnelle
              </p>
              <Link to="/contact" className="btn btn-primary mt-8">
                Estimer mes revenus Airbnb <ArrowRight size={16} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <ConciergerieExtras />

      <CTASection />
    </div>
  );
}

function ConciergerieExtras() {
  const [open, setOpen] = useState(0);
  return (
    <>
      {/* ZONES DESSERVIES */}
      <section className="section" style={{ background: "var(--cream)" }}>
        <div className="container-x">
          <SectionHeader
            eyebrow="Zones desservies"
            title="Votre conciergerie sur tout le littoral héraultais"
            sub="Cleanova-Sud gère vos locations saisonnières à Béziers et dans les stations balnéaires environnantes."
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
            {ZONES.map((z, i) => (
              <Reveal key={z.name} delay={i * 70}>
                <div className="flex items-start gap-3 p-6" style={{ background: "#fff", border: "1px solid var(--line)" }}>
                  <MapPin size={20} style={{ color: "var(--gold)", marginTop: 2 }} />
                  <div>
                    <div className="font-display text-xl">Conciergerie {z.name}</div>
                    <div className="text-sm mt-1" style={{ color: "var(--muted)" }}>{z.note}</div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <p className="text-center mt-8 text-sm" style={{ color: "var(--muted)" }}>
            Votre commune n'est pas listée ?{" "}
            <Link to="/contact" style={{ color: "var(--gold)" }}>Contactez-nous</Link>, nous étudions chaque demande.
          </p>
        </div>
      </section>

      {/* FAQ CONCIERGERIE */}
      <section className="section">
        <div className="container-x grid lg:grid-cols-3 gap-12">
          <div>
            <Eyebrow>Questions fréquentes</Eyebrow>
            <h2 className="display-title mt-4 text-4xl">Tout savoir sur la conciergerie Airbnb</h2>
            <p className="mt-5 leading-relaxed" style={{ color: "var(--muted)" }}>
              Vous hésitez à confier la gestion de votre location saisonnière ? Voici
              les réponses aux questions les plus posées par les propriétaires de Béziers
              et du littoral.
            </p>
            <Link to="/contact" className="btn btn-primary mt-7">
              Demander un devis gratuit <ArrowRight size={16} />
            </Link>
          </div>
          <div className="lg:col-span-2" data-testid="conciergerie-faq">
            {CONCIERGERIE_FAQ.map((item, i) => (
              <div className="acc-item" key={i}>
                <button
                  className="acc-trigger"
                  onClick={() => setOpen(open === i ? -1 : i)}
                  data-testid={`conc-faq-${i}`}
                >
                  {item.q}
                  {open === i ? (
                    <Minus size={22} style={{ color: "var(--gold)", flexShrink: 0 }} />
                  ) : (
                    <Plus size={22} style={{ color: "var(--gold)", flexShrink: 0 }} />
                  )}
                </button>
                <div
                  className="acc-body"
                  style={{ maxHeight: open === i ? 360 : 0, opacity: open === i ? 1 : 0 }}
                >
                  <p className="pb-6 pr-4">{item.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
